import { isNullOrUndefined } from 'common/util/type/typeUtil';
import { RubricType } from 'rubrics/src/components/rubricsTable/types';
import store from '../store';
import { RootState } from '../store/rootReducer';
import {
    getMarksModeForTestOrCommittee,
    GRADING_MODE, isCandidateInPrivateState,
    isOtherGraderNotSharedInWorkflowState,
    isPrivateMarksInWorkflowState,
    isGraderAtLeastPendingShareForCandidate,
} from './workflowService';
import { getEvaluatorById } from './committeeService';
import { getGradeTranslatedLabel } from '../util/gradingUtils';
import { CandidateLevelWorkflowState } from '../store/common/constants';
import { QuestionAccessLevel } from '../features/workflowSettings/constants';
import { getPartyCommitteeIdForContext } from './gradingPartyService';

export const isCommitteeTest = (): boolean => {
    const { assessment } = store.getState() as RootState;
    return assessment.committees && assessment.committees.length > 0;
};

export const isIndividualMarks = (): boolean => {
    const { context } = store.getState() as RootState;
    const partyCommitteeId = getPartyCommitteeIdForContext(context?.partyState?.gradingPartyDocument);
    const marksMode = getMarksModeForTestOrCommittee(partyCommitteeId);

    return marksMode === GRADING_MODE.INDIVIDUAL;
};

export const isSharedMarks = (): boolean => {
    const { context } = store.getState() as RootState;
    const partyCommitteeId = getPartyCommitteeIdForContext(context?.partyState?.gradingPartyDocument);

    const marksMode = getMarksModeForTestOrCommittee(partyCommitteeId);

    return marksMode === GRADING_MODE.COLLABORATIVE;
};

const isAlignMarksRequired = (): boolean => {
    const { assessment } = store.getState() as RootState;

    return !!assessment.gradingAgreeOnMarksEnabled;
};

export const isIndividualMarksPrivate = (isCandidateLevelGrading: boolean, candidateLevelWorkflowState: CandidateLevelWorkflowState): boolean => {
    return isIndividualMarks()
        && (isCandidateLevelGrading ? isCandidateInPrivateState(candidateLevelWorkflowState) : isPrivateMarksInWorkflowState());
};

export const isNoAlignMarksRequiredAndIndividualMarksShared = (
    isCandidateLevelGrading: boolean,
    candidateLevelWorkflowState: CandidateLevelWorkflowState,
    evaluators: any[],
) => {
    // isOtherGraderNotSharedInWorkflowState, might only shared by current grader only
    // hasMoreThanOneGraders - means state is shared or else it would not receive
    // other evaluator grades from the server (backend handles this for candidate level grading also)
    return !isAlignMarksRequired() && isIndividualMarks()
        && (isOtherGraderNotSharedInWorkflowState() || hasMoreThanOneGraders(evaluators)
        || (isCandidateLevelGrading && isGraderAtLeastPendingShareForCandidate(candidateLevelWorkflowState)));
};

// hasMoreThanOneGraders - means state is shared or else.
export const hasMoreThanOneGraders = (evaluators: any[]): boolean => {
    return evaluators && evaluators.length > 1;
};

export const isScoreAligned = (evaluators: any[]): boolean => {
    return hasMoreThanOneGraders(evaluators)
        && evaluators.every((evaluator: any) => evaluator.score === evaluators[0].score);
};

export const isIndividualMarksSharedWithConflicts = (evaluators: any[]): boolean => {
    return isAlignMarksRequired() && isIndividualMarks() && !isScoreAligned(evaluators);
};

export const isIndividualMarksSharedWithoutConflicts = (evaluators: any[]): boolean => {
    return isAlignMarksRequired() && isIndividualMarks() && isScoreAligned(evaluators);
};

export const getDisplayScore = (
    evaluatorScore: number,
    isOverridden: boolean,
    currentAutomaticScore: number,
    isAutomaticQuestion: boolean,
    usedInUserQuestionList?: boolean,
    questionWeight?: number,
    rubricType?: RubricType,
): string | number => {
    const { assessment } = store.getState() as RootState;
    const { useGradingThresholds, gradeTypeValues } = assessment;
    const hasQuestionWeightAndMarksInUserQuestionList = usedInUserQuestionList && !isNullOrUndefined(questionWeight);

    if (evaluatorScore === undefined || rubricType === RubricType.QUALITATIVE) {
        if (!isNullOrUndefined(currentAutomaticScore) && isAutomaticQuestion) {
            return hasQuestionWeightAndMarksInUserQuestionList ? (currentAutomaticScore * questionWeight).toFixed(2) : currentAutomaticScore;
        }
        return '-';
    }

    if (!useGradingThresholds) {
        const grade = Object.keys(gradeTypeValues).find((key) => gradeTypeValues[key] === evaluatorScore);
        return getGradeTranslatedLabel(grade);
    }
    const customScore = hasQuestionWeightAndMarksInUserQuestionList ? (evaluatorScore * questionWeight).toFixed(2) : evaluatorScore;

    return isOverridden ? `${customScore}*` : customScore;
};

export const getEvaluatorNameById = (evaluatorId: number, partyCommitteeId: number): string => {
    const evaluator = getEvaluatorById(evaluatorId, partyCommitteeId);
    return evaluator && evaluator.name;
};

export const getScoredEvaluatorById = (evaluatorId: number, scoredEvaluators: any[]) => {
    return scoredEvaluators
        && scoredEvaluators.find((evaluator: any) => evaluator.evaluatorPartyId === evaluatorId || evaluator.evaluatorId === evaluatorId);
};

export const isGraderNowAllowedToScoreQuestion = (questionAssginments: any[], questionAccessLevel: string, userId: number): boolean => {
    return !isCurrentUserAssigned(userId, questionAssginments) && (
        questionAccessLevel === QuestionAccessLevel.VIEW_OWN_MARK_OWN
        || questionAccessLevel === QuestionAccessLevel.VIEW_ALL_MARK_OWN);
};

export const isCurrentUserAssigned = (userId: number, questionAssginments: any[]) => {
    return questionAssginments && questionAssginments.length ? questionAssginments.includes(userId) : true;
};

export enum CriteriaLibraries {
    COMMON = 'Common',
    PERSONAL = 'Personal',
}
